import { useMemo, useState } from "react";

export interface BusinessMeetingTypeMappingRow {
  businessName: string;
  meetingTypes: string[];
}

interface BusinessMeetingTypeSelectorProps {
  mappings: BusinessMeetingTypeMappingRow[];
  onMappingsChange: (mappings: BusinessMeetingTypeMappingRow[]) => void;
  businesses: string[];
  meetingTypes: string[];
  error?: string;
}

function RowMeetingTypeDropdown({
  row,
  rowIndex,
  sortedMeetingTypes,
  onRowMeetingTypesChange
}: {
  row: BusinessMeetingTypeMappingRow;
  rowIndex: number;
  sortedMeetingTypes: string[];
  onRowMeetingTypesChange: (rowIndex: number, meetingTypes: string[]) => void;
}) {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const filteredMeetingTypes = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedMeetingTypes;
    return sortedMeetingTypes.filter((m) => m.toLowerCase().includes(q));
  }, [search, sortedMeetingTypes]);

  const selectedMeetingTypeSummary = useMemo(() => {
    if (row.meetingTypes.length === 0) return "Select meeting types";
    return row.meetingTypes.join(", ");
  }, [row.meetingTypes]);

  const toggleMeetingTypeSelection = (meetingTypeName: string) => {
    const isSelected = row.meetingTypes.includes(meetingTypeName);
    const next = isSelected
      ? row.meetingTypes.filter((m) => m !== meetingTypeName)
      : [...row.meetingTypes, meetingTypeName];
    onRowMeetingTypesChange(rowIndex, next);
  };

  return (
    <div className="relative" id={`user-meeting-types-${rowIndex}`}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="w-full border border-slate-300 px-3 py-2 text-left text-sm text-slate-900"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={`user-meeting-types-options-${rowIndex}`}
        aria-label="Meeting Type"
      >
        {selectedMeetingTypeSummary}
      </button>

      {isOpen && (
        <div className="absolute z-20 mt-1 w-full border border-slate-300 bg-white shadow-lg">
          <div className="border-b border-slate-200 p-2">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border border-slate-300 px-2 py-1 text-sm"
              placeholder="Search meeting type"
              aria-label="Search meeting type"
            />
          </div>

          <ul
            id={`user-meeting-types-options-${rowIndex}`}
            role="listbox"
            aria-multiselectable="true"
            className="max-h-56 overflow-y-auto p-1"
          >
            {filteredMeetingTypes.length === 0 ? (
              <li className="px-2 py-1.5 text-xs text-slate-500">No meeting types found.</li>
            ) : (
              filteredMeetingTypes.map((meetingTypeName) => {
                const checked = row.meetingTypes.includes(meetingTypeName);
                return (
                  <li key={meetingTypeName} role="option" aria-selected={checked}>
                    <label className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-sm hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleMeetingTypeSelection(meetingTypeName)}
                        className="h-4 w-4"
                      />
                      <span>{meetingTypeName}</span>
                    </label>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export function BusinessMeetingTypeSelector({
  mappings,
  onMappingsChange,
  businesses,
  meetingTypes,
  error
}: BusinessMeetingTypeSelectorProps) {
  const sortedBusinesses = useMemo(
    () => [...businesses].sort((a, b) => a.localeCompare(b)),
    [businesses]
  );

  const sortedMeetingTypes = useMemo(
    () => [...meetingTypes].sort((a, b) => a.localeCompare(b)),
    [meetingTypes]
  );

  const addRow = () => {
    onMappingsChange([
      ...mappings,
      {
        businessName: "",
        meetingTypes: [...sortedMeetingTypes]
      }
    ]);
  };

  const removeRow = (rowIndex: number) => {
    onMappingsChange(mappings.filter((_, index) => index !== rowIndex));
  };

  const updateRowBusiness = (rowIndex: number, businessName: string) => {
    onMappingsChange(
      mappings.map((row, index) => (index === rowIndex ? { ...row, businessName } : row))
    );
  };

  const updateRowMeetingTypes = (rowIndex: number, rowMeetingTypes: string[]) => {
    onMappingsChange(
      mappings.map((row, index) =>
        index === rowIndex ? { ...row, meetingTypes: rowMeetingTypes } : row
      )
    );
  };

  const availableBusinessesForRow = (rowIndex: number) => {
    const alreadySelectedByOtherRows = new Set(
      mappings
        .filter((_, index) => index !== rowIndex)
        .map((row) => row.businessName)
        .filter(Boolean)
    );
    return sortedBusinesses.filter((b) => !alreadySelectedByOtherRows.has(b));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-slate-800">Business & Meeting Types</label>
        <button
          type="button"
          onClick={addRow}
          className="border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
        >
          Add Mapping
        </button>
      </div>

      {mappings.map((row, rowIndex) => (
        <div key={`${row.businessName}-${rowIndex}`} className="space-y-2 border border-slate-200 p-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-600">{row.businessName || "Select business"}</span>
            {mappings.length > 1 && (
              <button
                type="button"
                onClick={() => removeRow(rowIndex)}
                className="text-xs text-red-700 hover:underline"
              >
                Remove
              </button>
            )}
          </div>

          <div>
            <label htmlFor={`user-businesses-${rowIndex}`} className="mb-1 block text-xs font-medium text-slate-700">
              Business
            </label>
            <select
              id={`user-businesses-${rowIndex}`}
              value={row.businessName}
              onChange={(e) => updateRowBusiness(rowIndex, e.target.value)}
              className="w-full border border-slate-300 px-3 py-2 text-sm text-slate-900"
              aria-label={`Business ${rowIndex + 1}`}
            >
              <option value="">Select business</option>
              {availableBusinessesForRow(rowIndex).map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor={`user-meeting-types-${rowIndex}`} className="mb-1 block text-xs font-medium text-slate-700">
              Meeting Type
            </label>
            <RowMeetingTypeDropdown
              row={row}
              rowIndex={rowIndex}
              sortedMeetingTypes={sortedMeetingTypes}
              onRowMeetingTypesChange={updateRowMeetingTypes}
            />
          </div>
        </div>
      ))}

      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
